
import { useEffect, useRef, useState } from 'preact/hooks'

import { inveniasClient } from './../utility/inveniasClient'
import { formData } from './../utility/formData'
import { map, filter, hasKeys } from './../utility/object'
import { unique } from './../utility/array'
import { json } from './../utility/dom'
import { debounce } from './../utility/debounce'


export const initialSessionFromDom = () => {
	const session = json('#form-session')
	return session || {}
}


const getAutofilledFields = (form = document) => {
	if (!form) return []
	const selectors = [':-webkit-autofill', ':autofill']
	for (const selector of selectors) {
		try { 
			return [...form.querySelectorAll(selector)] 
		} catch (e) {}
	}
	return []
}


const identity = x => x
const noop = () => {}


export function useSubmission({ session, beforeSubmit = identity, onSuccess = noop, onError = noop }) {

	const [fields, setFields] = useState(session.data || {})
	const [expires, setExpires] = useState(session.expires)
	const [validation, setValidation] = useState({})
	const [processing, setProcessing] = useState(false)
	const [submitted, setSubmitted] = useState(false)
	const client = inveniasClient(session.name, session.apiUpdateUrl, session.apiDocumentUrl)


	// external effects

	useEffect(() => submitted && onSuccess(fields), [submitted])
	useEffect(() => hasKeys(filter(validation)) && onError(fields), [processing])


	// send data to API

	const updateFields = (object, callback = noop) => {
		setFields(state => ({...state, ...object }))
		client.send(object).then(response => {
			setExpires(response.expires)
			const validations = map(object, (val, key) => response.validation?.[key] || undefined)	
			setValidation(state => ({ ...state, ...validations }))
			const success = response.success && !hasKeys(filter(validations))
			callback(success)
		})
	}

	const updateDocument = ({ name, file, setProgress, setProcessing, setComplete, setValue, setError, inveniasParse = false }) => {
		client
			.sendDocument(name, file, {
				inveniasParse: inveniasParse,
				onUploadProgress: progress => setProgress(progress),
				onUpload: event => setProcessing(true),
			})
			.then(response => {
				setFields(state => ({...state, [name]: response.data }))
				setExpires(response.expires)
				setValidation(state => ({ ...state, [name]: response.validation?.[name] || undefined }))
				setValue(response?.data?.documentId)
				setComplete(true)
			})
			.catch(response => {
				setFields(state => ({...state, [name]: undefined }))
				setExpires(response.expires)
				setError(response.validation[name] || response.error && response.description || 'Count not connect')
			})
	}

	const removeDocument = ({ name }) => {
		setFields(state => ({...state, [name]: undefined }))
		updateFields({ [name]: null })
	}

	const extendSession = () => updateFields({})


	// event handlers for form fields

	const { debounce: onInputDebounced, immediate: onInput } = debounce(event => {
		// get all currently autofilled fields, as well as the currently edited item
		// filter all form fields by these name (so gets radio/checkbox fields)
		const autofilled = getAutofilledFields(event.target.form)
		const names = unique([event.target, ...autofilled]).map(input => input.name)
		const data = formData([...event.target.form.elements], names)
		updateFields(data)
	}, 500)


	// event handlers for form

	const onSubmit = event => {
		event.preventDefault()
		const fields = fullFormState(event.currentTarget)
		const fieldsWithUserData = beforeSubmit(fields)
		setProcessing(true)
		updateFields(fieldsWithUserData, success => {
			setProcessing(false)
			if (success) setSubmitted(true)
		})
	}

	const onSubmitComplete = event => {
		event.preventDefault()
		const fields = fullFormState(event.currentTarget)
		const fieldsWithUserData = beforeSubmit(fields)
		setProcessing(true)
		updateFields(fieldsWithUserData, success => {
			if (!success) return setProcessing(false)

			fetch(session.apiSubmitUrl, { 
				method: 'POST', 
				body: JSON.stringify({ session: session.name })
			})
			.then(response => response.json())
			.then(response => {
				setProcessing(false)
				if (response.success) {
					setSubmitted(true)
				} else {
					setValidation({ validation, ...response.validation })
				}				
			})
			.catch(response => {
				setValidation({ validation, ...response.validation })
				setProcessing(false)
			})
		})
	}

	const resetSubmit = () => {
		setProcessing(false)
		setSubmitted(false)
	}

	const fullFormState = form => {
		const elements = [...form.elements]
		const state = formData(elements)

		// use state for document uploads, rather than the text value
		const files = elements.filter(input => input.name && input.type === 'file')
		files.map(file => {
			state[file.name] = fields[file.name] || false
		})
		return state
	}

	return {
		expires, 
		fields, 
		validation, 
		processing, 
		submitted, 
		onInput,
		onInputDebounced, 
		updateDocument,
		removeDocument,
		onSubmit,
		onSubmitComplete,
		extendSession,
		resetSubmit
	}
}
